import { FilesetResolver, ImageSegmenter } from "@mediapipe/tasks-vision";
import { derived, writable } from "svelte/store";

export type PhysicalPresenceState = "disabled" | "unknown" | "present" | "absent";
export type PhysicalPresenceActivationState = "enabled" | "disabled" | "error";

export const PHYSICAL_PRESENCE_PUBLIC_VARIABLE = "mercadologicaPhysicalPresence";
export const PHYSICAL_PRESENCE_ABSENT_SINCE_VARIABLE = "mercadologicaPhysicalPresenceAbsentSince";
export const PHYSICAL_PRESENCE_AGENT_VARIABLE = "mercadologicaPresenceAgentAvailable";

const STORAGE_KEY = "mercadologicaPhysicalPresenceEnabled";
const ABSENCE_DELAY_MS = 60_000;
const RETURN_CONFIRMATION_MS = 3_000;
const DETECTION_INTERVAL_MS = 2_000;
const PERSON_CONFIDENCE_THRESHOLD = 0.55;
const MINIMUM_PERSON_PIXEL_RATIO = 0.015;

type DeviceState = "active" | "idle" | "unknown";
type ScreenState = "locked" | "unlocked" | "unknown";

interface IdleDetectorLike extends EventTarget {
    userState: "active" | "idle" | null;
    screenState: "locked" | "unlocked" | null;
    start(options: { threshold: number; signal: AbortSignal }): Promise<void>;
}

interface IdleDetectorConstructor {
    new (): IdleDetectorLike;
    requestPermission(): Promise<"granted" | "denied">;
}

function wasEnabled(): boolean {
    return typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY) === "true";
}

const enabledStore = writable(wasEnabled());
export const physicalPresenceEnabledStore = { subscribe: enabledStore.subscribe };
export const physicalPresenceStore = writable<PhysicalPresenceState>(wasEnabled() ? "unknown" : "disabled");
export const physicalPresenceErrorStore = writable<string | null>(null);
export const physicalPresenceAbsentSinceStore = writable<number | null>(null);
export const physicalPresenceAgentAvailableStore = writable<boolean | null>(null);
export const physicalPresenceActivationStore = derived(
    [enabledStore, physicalPresenceErrorStore],
    ([$enabled, $error]): PhysicalPresenceActivationState => {
        // A missing signal (for example, OS-wide idle detection in Firefox) is
        // only a warning while another signal is working.
        if ($enabled) {
            return "enabled";
        }
        if ($error) {
            return "error";
        }
        return "disabled";
    },
);

class PhysicalPresenceController {
    private stream: MediaStream | null = null;
    private video: HTMLVideoElement | null = null;
    private segmenter: ImageSegmenter | null = null;
    private timer: ReturnType<typeof setTimeout> | null = null;
    private idleAbortController: AbortController | null = null;
    private idleDetector: IdleDetectorLike | null = null;
    private stopped = false;
    private processing = false;
    private lastTimestampMs = -1;
    private cameraAvailable = false;
    private idleDetectionAvailable = false;
    private cameraState: "present" | "absent" | "unknown" = "unknown";
    private deviceState: DeviceState = "unknown";
    private screenState: ScreenState = "unknown";
    private cameraAbsentSince: number | null = null;
    private cameraPresentSince: number | null = null;
    private lastLocalActivityAt = Date.now();
    private lastLocalActivityPublishedAt = 0;

    private agentTimer: ReturnType<typeof setInterval> | null = null;
    private agentAvailable = false;
    private agentIdleMs: number | null = null;

    private readonly onLocalActivity = (): void => {
        const now = Date.now();
        this.lastLocalActivityAt = now;

        // Pointer movement can fire dozens of times per second. Updating the
        // Svelte store once per second is enough while keeping the timestamp exact.
        if (now - this.lastLocalActivityPublishedAt < 1_000) {
            return;
        }
        this.lastLocalActivityPublishedAt = now;
        if (this.idleDetectionAvailable) {
            this.deviceState = "active";
        }
        physicalPresenceStore.set("present");
    };

    async start(): Promise<void> {
        physicalPresenceStore.set("unknown");
        physicalPresenceErrorStore.set(null);
        this.addLocalActivityListeners();

        await this.pollPresenceAgent();
        this.startPresenceAgentPolling();

        const [idleResult, cameraResult] = await Promise.allSettled([
            this.startIdleDetection(),
            this.startCameraDetectionWithRetry(),
        ]);

        this.idleDetectionAvailable = idleResult.status === "fulfilled" && idleResult.value;
        this.cameraAvailable = cameraResult.status === "fulfilled" && cameraResult.value;

        if (!this.cameraAvailable && !this.idleDetectionAvailable && !this.agentAvailable) {
            throw new Error("Não foi possível acessar a câmera nem detectar a atividade do computador.");
        }

        if (!this.cameraAvailable) {
            physicalPresenceErrorStore.set(
                "A câmera está indisponível. A presença automática continua ativa pelo uso do computador.",
            );
        } else if (!this.idleDetectionAvailable && !this.agentAvailable) {
            physicalPresenceErrorStore.set(
                "Este navegador não permite detectar mouse e teclado em outros programas e o Agente Windows não foi detectado. A presença automática continua ativa pela câmera.",
            );
        }

        this.evaluatePresence();
    }

    stop(): void {
        this.stopped = true;
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        this.idleAbortController?.abort();
        this.idleAbortController = null;

        if (this.agentTimer !== null) {
            clearInterval(this.agentTimer);
            this.agentTimer = null;
        }

        this.removeLocalActivityListeners();
        this.stream?.getTracks().forEach((track) => track.stop());
        this.stream = null;
        if (this.video) {
            this.video.pause();
            this.video.srcObject = null;
            this.video = null;
        }
        this.segmenter?.close();
        this.segmenter = null;
    }

    private addLocalActivityListeners(): void {
        window.addEventListener("pointermove", this.onLocalActivity, { passive: true, capture: true });
        window.addEventListener("pointerdown", this.onLocalActivity, { passive: true, capture: true });
        window.addEventListener("keydown", this.onLocalActivity, { passive: true, capture: true });
        window.addEventListener("touchstart", this.onLocalActivity, { passive: true, capture: true });
    }

    private removeLocalActivityListeners(): void {
        window.removeEventListener("pointermove", this.onLocalActivity, true);
        window.removeEventListener("pointerdown", this.onLocalActivity, true);
        window.removeEventListener("keydown", this.onLocalActivity, true);
        window.removeEventListener("touchstart", this.onLocalActivity, true);
    }

    private startPresenceAgentPolling(): void {
        if (this.agentTimer !== null) {
            clearInterval(this.agentTimer);
        }

        this.agentTimer = setInterval(() => {
            void this.pollPresenceAgent();
        }, 2_000);
    }

    private async pollPresenceAgent(): Promise<void> {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 1_000);

        try {
            const response = await fetch("http://127.0.0.1:8765/activity", {
                method: "GET",
                cache: "no-store",
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = (await response.json()) as {
                ok?: boolean;
                idleMs?: number;
            };

            if (data.ok !== true || typeof data.idleMs !== "number" || !Number.isFinite(data.idleMs)) {
                throw new Error("Resposta inválida do Agente Windows.");
            }

            this.agentAvailable = true;
            this.agentIdleMs = Math.max(0, data.idleMs);
            physicalPresenceAgentAvailableStore.set(true);

            this.evaluatePresence();
        } catch {
            this.agentAvailable = false;
            this.agentIdleMs = null;
            physicalPresenceAgentAvailableStore.set(false);
        } finally {
            window.clearTimeout(timeout);
        }
    }

    private async startIdleDetection(): Promise<boolean> {
        const IdleDetectorClass = (
            window as unknown as {
                IdleDetector?: IdleDetectorConstructor;
            }
        ).IdleDetector;

        if (!IdleDetectorClass) {
            return false;
        }

        const permission = await IdleDetectorClass.requestPermission();
        if (permission !== "granted") {
            return false;
        }

        this.idleAbortController = new AbortController();
        this.idleDetector = new IdleDetectorClass();
        this.idleDetector.addEventListener("change", () => {
            void this.handleIdleChange();
        });
        await this.idleDetector.start({
            threshold: ABSENCE_DELAY_MS,
            signal: this.idleAbortController.signal,
        });
        await this.handleIdleChange();
        return true;
    }

    private async handleIdleChange(): Promise<void> {
        if (!this.idleDetector || this.stopped) {
            return;
        }

        this.deviceState = this.idleDetector.userState ?? "unknown";
        this.screenState = this.idleDetector.screenState ?? "unknown";

        if (this.screenState === "locked") {
            physicalPresenceStore.set("absent");
            return;
        }

        if (this.deviceState === "active") {
            physicalPresenceStore.set("present");
            return;
        }

        // When the operating system reports 60 seconds of inactivity, obtain a
        // fresh camera reading before deciding whether the person is absent.
        await this.analyzeCameraFrame();
        this.evaluatePresence();
    }

    private async startCameraDetectionWithRetry(): Promise<boolean> {
        try {
            return await this.startCameraDetection();
        } catch (firstError) {
            console.warn("[PhysicalPresence] Camera initialization failed; retrying once.", firstError);
            this.stream?.getTracks().forEach((track) => track.stop());
            this.stream = null;
            if (this.video) {
                this.video.pause();
                this.video.srcObject = null;
                this.video = null;
            }
            this.segmenter?.close();
            this.segmenter = null;

            await new Promise<void>((resolve) => setTimeout(resolve, 1_500));
            if (this.stopped) {
                return false;
            }
            return this.startCameraDetection();
        }
    }

    private async startCameraDetection(): Promise<boolean> {
        this.stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
                width: { ideal: 320 },
                height: { ideal: 240 },
                frameRate: { ideal: 2, max: 5 },
                facingMode: "user",
            },
        });

        this.video = document.createElement("video");
        this.video.autoplay = true;
        this.video.muted = true;
        this.video.playsInline = true;
        this.video.srcObject = this.stream;
        await this.video.play();

        const filesetResolver = await FilesetResolver.forVisionTasks("./static/tasksVision/wasm");
        const canvas = document.createElement("canvas");
        canvas.width = 320;
        canvas.height = 240;

        try {
            this.segmenter = await ImageSegmenter.createFromOptions(filesetResolver, {
                baseOptions: {
                    modelAssetPath: "./static/tasksVision/selfie_segmenter.tflite",
                    delegate: "GPU",
                },
                canvas,
                runningMode: "VIDEO",
                outputCategoryMask: false,
                outputConfidenceMasks: true,
            });
        } catch (gpuError) {
            console.warn("[PhysicalPresence] GPU unavailable; using CPU.", gpuError);
            this.segmenter = await ImageSegmenter.createFromOptions(filesetResolver, {
                baseOptions: {
                    modelAssetPath: "./static/tasksVision/selfie_segmenter.tflite",
                    delegate: "CPU",
                },
                canvas,
                runningMode: "VIDEO",
                outputCategoryMask: false,
                outputConfidenceMasks: true,
            });
        }

        this.scheduleNextFrame(0);
        return true;
    }

    private scheduleNextFrame(delay = DETECTION_INTERVAL_MS): void {
        if (this.stopped) {
            return;
        }
        if (this.timer !== null) {
            clearTimeout(this.timer);
        }
        this.timer = setTimeout(() => {
            void this.analyzeCameraFrame().finally(() => this.scheduleNextFrame());
        }, delay);
    }

    private async analyzeCameraFrame(): Promise<void> {
        if (
            this.stopped ||
            this.processing ||
            !this.video ||
            !this.segmenter ||
            this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
        ) {
            return;
        }

        this.processing = true;
        try {
            const timestampMs = Math.max(performance.now(), this.lastTimestampMs + 1);
            this.lastTimestampMs = timestampMs;
            const result = this.segmenter.segmentForVideo(this.video, timestampMs);
            try {
                const mask = result.confidenceMasks?.[0];
                if (!mask) {
                    return;
                }
                const values = mask.getAsFloat32Array();
                const sampleStep = Math.max(1, Math.floor(values.length / 4_000));
                let personPixels = 0;
                let sampledPixels = 0;

                for (let index = 0; index < values.length; index += sampleStep) {
                    sampledPixels++;
                    if (values[index] >= PERSON_CONFIDENCE_THRESHOLD) {
                        personPixels++;
                    }
                }

                this.recordCameraResult(personPixels / sampledPixels >= MINIMUM_PERSON_PIXEL_RATIO);
            } finally {
                result.close();
            }
        } catch (error) {
            console.warn("[PhysicalPresence] Camera frame analysis failed.", error);
        } finally {
            this.processing = false;
        }
    }

    private recordCameraResult(personDetected: boolean): void {
        const now = Date.now();
        if (personDetected) {
            this.cameraState = "present";
            this.cameraAbsentSince = null;
            this.cameraPresentSince ??= now;
        } else {
            this.cameraState = "absent";
            this.cameraPresentSince = null;
            this.cameraAbsentSince ??= now;
        }
        this.evaluatePresence();
    }

    private evaluatePresence(): void {
        const now = Date.now();

        if (this.screenState === "locked") {
            physicalPresenceStore.set("absent");
            return;
        }

        // In browsers without the system Idle Detection API (notably Firefox),
        // activity inside WorkAdventure must keep the user present for the full
        // absence window even when the camera cannot see a person.
        if (now - this.lastLocalActivityAt < ABSENCE_DELAY_MS) {
            physicalPresenceStore.set("present");
            return;
        }

        // O Agente Windows usa GetLastInputInfo e consegue detectar atividade
        // mesmo quando Word, Excel ou outra aplicação está em primeiro plano.
        if (
            this.agentAvailable &&
            this.agentIdleMs !== null &&
            this.agentIdleMs < ABSENCE_DELAY_MS
        ) {
            physicalPresenceStore.set("present");
            return;
        }

        if (this.deviceState === "active") {
            physicalPresenceStore.set("present");
            return;
        }

        if (
            this.cameraState === "present" &&
            this.cameraPresentSince !== null &&
            now - this.cameraPresentSince >= RETURN_CONFIRMATION_MS
        ) {
            physicalPresenceStore.set("present");
            return;
        }

        if (this.deviceState === "idle" && (this.cameraState === "absent" || !this.cameraAvailable)) {
            physicalPresenceStore.set("absent");
            return;
        }

        // Firefox não possui IdleDetector. Se não houver câmera, o próprio
        // agente pode determinar a ausência após 60 segundos sem atividade.
        if (
            this.agentAvailable &&
            this.agentIdleMs !== null &&
            this.agentIdleMs >= ABSENCE_DELAY_MS &&
            !this.cameraAvailable
        ) {
            physicalPresenceStore.set("absent");
            return;
        }

        if (
            !this.idleDetectionAvailable &&
            this.cameraState === "absent" &&
            this.cameraAbsentSince !== null &&
            now - this.cameraAbsentSince >= ABSENCE_DELAY_MS
        ) {
            physicalPresenceStore.set("absent");
        }
    }
}

let controller: PhysicalPresenceController | null = null;

async function startController(): Promise<void> {
    controller?.stop();
    controller = new PhysicalPresenceController();
    try {
        await controller.start();
    } catch (error) {
        console.error("[PhysicalPresence] Could not start automatic presence.", error);
        controller.stop();
        controller = null;
        enabledStore.set(false);
        physicalPresenceStore.set("disabled");
        physicalPresenceErrorStore.set(error instanceof Error ? error.message : "Falha ao ativar a presença automática.");
        window.localStorage.setItem(STORAGE_KEY, "false");
    }
}

export async function setPhysicalPresenceEnabled(enabled: boolean): Promise<void> {
    enabledStore.set(enabled);
    window.localStorage.setItem(STORAGE_KEY, String(enabled));

    if (enabled) {
        await startController();
        return;
    }

    controller?.stop();
    controller = null;
    physicalPresenceStore.set("disabled");
    physicalPresenceErrorStore.set(null);
}

if (wasEnabled()) {
    setTimeout(() => {
        void startController();
    }, 0);
}

let latestActivationState: PhysicalPresenceActivationState = wasEnabled() ? "enabled" : "disabled";
let latestAbsentSince: number | null = null;
let latestAgentAvailable: boolean | null = null;
let publishRetryTimer: ReturnType<typeof setTimeout> | null = null;

async function publishPresenceData(): Promise<void> {
    if (typeof window === "undefined") {
        return;
    }

    try {
        // Dynamic import avoids a static dependency cycle: MediaStore imports this
        // module and GameManager indirectly depends on MediaStore.
        const { gameManager } = await import("../Phaser/Game/GameManager");
        const manager = gameManager.getCurrentGameScene().getPlayerVariablesManager();
        if (!manager) {
            schedulePublishRetry();
            return;
        }

        manager.setVariableFromApp(PHYSICAL_PRESENCE_PUBLIC_VARIABLE, latestActivationState, {
            persist: false,
            public: true,
            scope: "world",
            force: true,
        });
        manager.setVariableFromApp(PHYSICAL_PRESENCE_ABSENT_SINCE_VARIABLE, latestAbsentSince, {
            persist: false,
            public: true,
            scope: "world",
            force: true,
        });

        manager.setVariableFromApp(PHYSICAL_PRESENCE_AGENT_VARIABLE, latestAgentAvailable, {
            persist: false,
            public: true,
            scope: "world",
            force: true,
        });
    } catch {
        // The game scene may not exist yet during startup or a room transition.
        schedulePublishRetry();
    }
}

function schedulePublishRetry(): void {
    if (publishRetryTimer !== null || typeof window === "undefined") {
        return;
    }
    publishRetryTimer = setTimeout(() => {
        publishRetryTimer = null;
        void publishPresenceData();
    }, 2_000);
}

if (typeof window !== "undefined") {
    physicalPresenceActivationStore.subscribe((state) => {
        latestActivationState = state;
        void publishPresenceData();
    });

    physicalPresenceAgentAvailableStore.subscribe((available) => {
        latestAgentAvailable = available;
        void publishPresenceData();
    });

    let previousPresenceState: PhysicalPresenceState | null = null;
    physicalPresenceStore.subscribe((state) => {
        if (state === "absent" && previousPresenceState !== "absent") {
            latestAbsentSince = Date.now();
            physicalPresenceAbsentSinceStore.set(latestAbsentSince);
        } else if (state !== "absent" && latestAbsentSince !== null) {
            latestAbsentSince = null;
            physicalPresenceAbsentSinceStore.set(null);
        }
        previousPresenceState = state;
        void publishPresenceData();
    });

    // Republish periodically so the value is also available after room changes.
    window.setInterval(() => {
        void publishPresenceData();
    }, 15_000);
}
